import { useEffect, useState } from 'react'
import { can } from '../../lib/api'
import { exitApi, type ExitSummary, type ExitView } from '../../lib/exit'
import { personLabel, usePeopleNames } from '../../lib/people'
import { ErrorBox, Loading } from '../DataTable'
import { StageTrack, Status } from '../kit'
import { Avatar } from '../ui'
import { loadVendorSummary } from './board'
import { chainTrack } from './ExitStagePanel'
import { TRIGGER_WORDS, dayMonth, dayMonthYear, exitWord, stagePosition } from './words'
import '../../styles/exit.css'

// The vendor record's Exit section: where the exit is, in three lines, and the
// way into its workspace.
//
// # WHY ONLY A SUMMARY HERE
//
// The whole exit used to be drawn in this section, in the column beside the
// section menu, and read as basic there. It has its own page now. The record
// keeps the answer to "is this vendor leaving, and where has it got to" (the
// track, who has it next, one status) because that is what somebody reading the
// vendor asks, and everything done about the exit is one press away.

export function ExitSummaryCard({ vendorId }: { vendorId: string }) {
  const [view, setView] = useState<ExitView | null>(null)
  const [summary, setSummary] = useState<ExitSummary | null>(null)
  const [error, setError] = useState<unknown>(null)
  const names = usePeopleNames()
  const label = (s: string | undefined) => personLabel(names, s)

  useEffect(() => {
    let live = true
    exitApi
      .vendorExit(vendorId)
      .then((v) => {
        if (live) setView(v)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    void loadVendorSummary(vendorId).then((s) => {
      if (live) setSummary(s)
    })
    return () => {
      live = false
    }
  }, [vendorId])

  if (error != null) return <ErrorBox error={error} what="The offboarding" />
  if (view == null) return <Loading what="the offboarding" />

  const chain = view.exitApproval
  const exit = chain.exit
  const proposal = exit?.status === 'PROPOSED' ? exit : null
  const underWay = view.status === 'OFFBOARDING'
  const signedOut = view.status === 'OFFBOARDED'
  const word = exitWord(summary, view.status, !!proposal, chain.met, can('offboarding.run'))
  const at = stagePosition(chain.stages)
  const current = underWay ? chain.stages[at] : undefined

  let line
  if (current) {
    line = (
      <>
        <Avatar name={current.job} size={26} />
        <span>
          <b>
            Next: {current.job}, stage {at + 1} of {chain.total}
          </b>
          {summary?.blocker ? ', blocked by ' + summary.blocker : summary?.dueOn ? ', due ' + dayMonth(summary.dueOn) : ''}
        </span>
      </>
    )
  } else if (underWay) {
    line = <span>Every stage is signed. Close offboarding in the workspace</span>
  } else if (proposal) {
    line = (
      <span>
        <b>Proposed: {TRIGGER_WORDS[proposal.triggerKind] ?? 'by hand'}.</b> The decision is made in the offboarding workspace
      </span>
    )
  } else if (signedOut) {
    line = <span>Closed{exit?.closedAt ? ' ' + dayMonthYear(exit.closedAt) : ''}. The dossier is in the workspace</span>
  } else {
    line = <span>No offboarding. The chain it would be held to today</span>
  }

  return (
    <section className="panel exit-compact" aria-labelledby="exit-compact-h">
      <div className="exit-panel-head">
        <h2 id="exit-compact-h" className="exit-grow">
          Offboarding sign-off
        </h2>
        <Status tone={word.tone} title={summary?.blocker}>
          {word.text}
        </Status>
      </div>
      {chain.total > 0 && (
        <div className={'exit-track' + (underWay || signedOut ? '' : ' exit-track-preview')}>
          <StageTrack stages={chainTrack(chain, summary, underWay, label)} label={'Offboarding sign-off, ' + chain.total + ' stages'} />
        </div>
      )}
      <div className="exit-compact-foot">
        <p className="exit-compact-next">{line}</p>
        <a className="button primary" href={'#/exits/' + vendorId}>
          Open offboarding
        </a>
      </div>
    </section>
  )
}
